import { useEffect, useRef, useState, useCallback } from "react";
import { useAuth, API, TOKEN_KEY } from "@/hooks/useAuth";

// Global singleton so multiple components share one WebSocket per tab.
let _socket = null;
const _listeners = new Set();
const _state = {
  ready: false,
  presence: [],
  profile: null,
  reconnectMs: 1000,
};

function notify() {
  _listeners.forEach((cb) => cb({ ..._state }));
}

function buildWsUrl(token) {
  const base = API.replace(/\/api$/, "");
  return `${base.replace(/^http/, "ws")}/api/ws?token=${encodeURIComponent(token)}`;
}

function ensureSocket(token, onMessage) {
  if (_socket && (_socket.readyState === WebSocket.OPEN || _socket.readyState === WebSocket.CONNECTING)) {
    return _socket;
  }
  if (!token) return null;
  const ws = new WebSocket(buildWsUrl(token));
  _socket = ws;
  ws.onopen = () => {
    _state.ready = true;
    _state.reconnectMs = 1000;
    notify();
  };
  ws.onclose = () => {
    _state.ready = false;
    _socket = null;
    notify();
    // Reconnect with backoff.
    const wait = Math.min(_state.reconnectMs, 15000);
    setTimeout(() => {
      const t = localStorage.getItem(TOKEN_KEY);
      if (t) ensureSocket(t, onMessage);
    }, wait);
    _state.reconnectMs = Math.min(_state.reconnectMs * 1.6, 15000);
  };
  ws.onmessage = (evt) => {
    try {
      const data = JSON.parse(evt.data);
      if (data.type === "PRESENCE") {
        _state.presence = data.users || [];
        notify();
      } else if (data.type === "READY") {
        _state.profile = data.profile;
        notify();
      }
      _listeners.forEach((cb) => cb({ ..._state, message: data }));
    } catch {
      /* ignore malformed */
    }
  };
  return ws;
}

export function useSocket() {
  const { user } = useAuth();
  const [snapshot, setSnapshot] = useState({ ..._state });
  const cbRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!user || !token) return undefined;
    cbRef.current = (s) => setSnapshot(s);
    _listeners.add(cbRef.current);
    ensureSocket(token);
    return () => {
      _listeners.delete(cbRef.current);
    };
  }, [user]);

  const send = useCallback((payload) => {
    if (_socket && _socket.readyState === WebSocket.OPEN) {
      _socket.send(JSON.stringify(payload));
      return true;
    }
    return false;
  }, []);

  const subscribe = useCallback((handler) => {
    _listeners.add(handler);
    return () => _listeners.delete(handler);
  }, []);

  return {
    ready: snapshot.ready,
    presence: snapshot.presence,
    profile: snapshot.profile,
    send,
    subscribe,
  };
}

export function closeSocket() {
  if (_socket) {
    try { _socket.close(); } catch { /* ignore */ }
    _socket = null;
  }
}
